<script setup lang="ts">
/**
 * `ConfirmationHost.vue` — renders the head of `IConfirmationModel.requests`.
 *
 * The web equivalent of Unity's navigation frame pushing
 * `ViewNames.Confirmation` / `ViewNames.ConfirmationDark` additively: any model
 * or composable can `request(...)` from anywhere, and this single mount (in
 * `App.vue`, above every route) puts the dialog on screen and answers it.
 *
 * One at a time, in arrival order — Unity stacks additive navigations, but
 * stacked modals read as broken on the web.
 *
 * The `cf-dialog` transition is applied HERE rather than inside
 * `ConfirmationView`, because this is where the `v-if` lives — and it is
 * already inside the `<Teleport>`, which is the arrangement a leave animation
 * needs (a `<Transition>` wrapped AROUND a teleporting component has no element
 * in place to hook). `mode="out-in"` so a confirmation raised while another is
 * dismissing waits its turn instead of cross-fading over it, which is the same
 * one-at-a-time rule the queue above already applies.
 */
import { onScopeDispose, ref } from "vue";
import { Ioc } from "@core/ioc/ioc";
import ConfirmationView from "./ConfirmationView.vue";
import ConfirmationDarkView from "./ConfirmationDarkView.vue";
import {
  CONFIRMATION_MODEL,
  type ConfirmationRequest,
  type ConfirmationResult,
  type IConfirmationModel,
} from "@models/notifications/IConfirmationModel";

defineOptions({ name: "ConfirmationHost" });

const model = Ioc.resolve<IConfirmationModel>(CONFIRMATION_MODEL);
const current = ref<ConfirmationRequest | null>(model.requests[0] ?? null);

const off = model.onChanged.on((requests) => {
  current.value = requests[0] ?? null;
});
onScopeDispose(off);

function onAnswer(result: ConfirmationResult, suppress: boolean): void {
  const request = current.value;
  if (!request) return;
  // Unity `SaveDoNotShowAgain()` runs on confirm, cancel AND undecided.
  const key = request.args.doNotShowAgainKey;
  if (key && suppress) model.suppress(key);
  model.answer(request.id, result);
}
</script>

<template>
  <Teleport to="body">
    <Transition name="cf-dialog" mode="out-in" appear>
      <component
        :is="current.args.theme === 'dark' ? ConfirmationDarkView : ConfirmationView"
        v-if="current"
        :key="current.id"
        :args="current.args"
        @answer="onAnswer"
      />
    </Transition>
  </Teleport>
</template>
